/**
 * Objetos do chat no formato da API v10 do Discord, como a API manda (REST e
 * gateway). Os montadores da API (`presentation/chat-objects.ts`) devolvem
 * estes tipos, então o compilador confere os dois lados do contrato; o
 * cliente (core) copia este arquivo.
 *
 * `type` (e não `interface`) de propósito: assim cabem num
 * `Record<string, unknown>` sem conversão.
 */

/** ID no formato do Discord, como texto (o mesmo de `snowflake.ts`). Sem import: os contratos são copiados um a um. */
type Snowflake = string;

// ---------------------------------------------------------------------------
// Números do Discord
// ---------------------------------------------------------------------------

export const ChannelType = {
  GUILD_TEXT: 0,
  DM: 1,
  GUILD_VOICE: 2,
  GROUP_DM: 3,
  GUILD_CATEGORY: 4,
  GUILD_ANNOUNCEMENT: 5,
  ANNOUNCEMENT_THREAD: 10,
  PUBLIC_THREAD: 11,
  PRIVATE_THREAD: 12,
  GUILD_STAGE_VOICE: 13,
  GUILD_DIRECTORY: 14,
  GUILD_FORUM: 15,
  GUILD_MEDIA: 16,
} as const;

export const MessageType = {
  DEFAULT: 0,
  RECIPIENT_ADD: 1,
  RECIPIENT_REMOVE: 2,
  CALL: 3,
  CHANNEL_NAME_CHANGE: 4,
  CHANNEL_ICON_CHANGE: 5,
  CHANNEL_PINNED_MESSAGE: 6,
  USER_JOIN: 7,
  GUILD_BOOST: 8,
  GUILD_BOOST_TIER_1: 9,
  GUILD_BOOST_TIER_2: 10,
  GUILD_BOOST_TIER_3: 11,
  CHANNEL_FOLLOW_ADD: 12,
  GUILD_DISCOVERY_DISQUALIFIED: 14,
  GUILD_DISCOVERY_REQUALIFIED: 15,
  THREAD_CREATED: 18,
  REPLY: 19,
  CHAT_INPUT_COMMAND: 20,
  THREAD_STARTER_MESSAGE: 21,
  GUILD_INVITE_REMINDER: 22,
  CONTEXT_MENU_COMMAND: 23,
  AUTO_MODERATION_ACTION: 24,
  ROLE_SUBSCRIPTION_PURCHASE: 25,
  STAGE_START: 27,
  STAGE_END: 28,
  STAGE_SPEAKER: 29,
  STAGE_TOPIC: 31,
  GUILD_INCIDENT_ALERT_MODE_ENABLED: 36,
  GUILD_INCIDENT_ALERT_MODE_DISABLED: 37,
  POLL_RESULT: 46,
} as const;

export const MessageFlags = {
  CROSSPOSTED: 1 << 0,
  IS_CROSSPOST: 1 << 1,
  SUPPRESS_EMBEDS: 1 << 2,
  SOURCE_MESSAGE_DELETED: 1 << 3,
  URGENT: 1 << 4,
  HAS_THREAD: 1 << 5,
  EPHEMERAL: 1 << 6,
  LOADING: 1 << 7,
  FAILED_TO_MENTION_SOME_ROLES_IN_THREAD: 1 << 8,
  SUPPRESS_NOTIFICATIONS: 1 << 12,
  IS_VOICE_MESSAGE: 1 << 13,
  HAS_SNAPSHOT: 1 << 14,
} as const;

export const ChannelFlags = {
  PINNED: 1 << 1,
  REQUIRE_TAG: 1 << 4,
  HIDE_MEDIA_DOWNLOAD_OPTIONS: 1 << 15,
} as const;

export const SystemChannelFlags = {
  SUPPRESS_JOIN_NOTIFICATIONS: 1 << 0,
  SUPPRESS_PREMIUM_SUBSCRIPTIONS: 1 << 1,
  SUPPRESS_GUILD_REMINDER_NOTIFICATIONS: 1 << 2,
  SUPPRESS_JOIN_NOTIFICATION_REPLIES: 1 << 3,
} as const;

/** Ações da auditoria (`action_type`) que o chat grava. */
export const AuditLogEvent = {
  GUILD_UPDATE: 1,
  CHANNEL_CREATE: 10,
  CHANNEL_UPDATE: 11,
  CHANNEL_DELETE: 12,
  CHANNEL_OVERWRITE_CREATE: 13,
  CHANNEL_OVERWRITE_UPDATE: 14,
  CHANNEL_OVERWRITE_DELETE: 15,
  MEMBER_KICK: 20,
  MEMBER_PRUNE: 21,
  MEMBER_BAN_ADD: 22,
  MEMBER_BAN_REMOVE: 23,
  MEMBER_UPDATE: 24,
  MEMBER_ROLE_UPDATE: 25,
  ROLE_CREATE: 30,
  ROLE_UPDATE: 31,
  ROLE_DELETE: 32,
  INVITE_CREATE: 40,
  INVITE_UPDATE: 41,
  INVITE_DELETE: 42,
  WEBHOOK_CREATE: 50,
  WEBHOOK_UPDATE: 51,
  WEBHOOK_DELETE: 52,
  EMOJI_CREATE: 60,
  EMOJI_UPDATE: 61,
  EMOJI_DELETE: 62,
  MESSAGE_DELETE: 72,
  MESSAGE_BULK_DELETE: 73,
  MESSAGE_PIN: 74,
  MESSAGE_UNPIN: 75,
  INTEGRATION_CREATE: 80,
  STICKER_CREATE: 90,
  STICKER_UPDATE: 91,
  STICKER_DELETE: 92,
  GUILD_SCHEDULED_EVENT_CREATE: 100,
  GUILD_SCHEDULED_EVENT_UPDATE: 101,
  GUILD_SCHEDULED_EVENT_DELETE: 102,
  THREAD_CREATE: 110,
  THREAD_UPDATE: 111,
  THREAD_DELETE: 112,
  AUTO_MODERATION_RULE_CREATE: 140,
  AUTO_MODERATION_RULE_UPDATE: 141,
  AUTO_MODERATION_RULE_DELETE: 142,
  AUTO_MODERATION_BLOCK_MESSAGE: 143,
  AUTO_MODERATION_FLAG_TO_CHANNEL: 144,
  AUTO_MODERATION_USER_COMMUNICATION_DISABLED: 145,
} as const;

/** Códigos de erro (`code` no corpo) que a API do chat devolve, os do Discord. */
export const ChatErrorCodes = {
  UNKNOWN_CHANNEL: 10003,
  UNKNOWN_GUILD: 10004,
  UNKNOWN_INVITE: 10006,
  UNKNOWN_MEMBER: 10007,
  UNKNOWN_MESSAGE: 10008,
  UNKNOWN_ROLE: 10011,
  UNKNOWN_USER: 10013,
  UNKNOWN_EMOJI: 10014,
  UNKNOWN_BAN: 10026,
  UNKNOWN_STICKER: 10060,
  UNKNOWN_AUTO_MODERATION_RULE: 10066,
  UNKNOWN_SCHEDULED_EVENT: 10070,
  SLOWMODE: 20016,
  MAX_GUILDS: 30001,
  MAX_PINS: 30003,
  MAX_ROLES: 30005,
  MAX_EMOJIS: 30008,
  MAX_REACTIONS: 30010,
  MAX_CHANNELS: 30013,
  MAX_INVITES: 30016,
  MAX_STICKERS: 30039,
  FILE_TOO_LARGE: 40005,
  BANNED: 40007,
  MISSING_ACCESS: 50001,
  CANNOT_EDIT_OTHERS: 50005,
  EMPTY_MESSAGE: 50006,
  CANNOT_MESSAGE_USER: 50007,
  NON_TEXT_CHANNEL: 50008,
  MISSING_PERMISSIONS: 50013,
  SYSTEM_MESSAGE: 50021,
  WRONG_CHANNEL_TYPE: 50024,
  INVALID_ROLE: 50028,
  TOO_OLD_TO_BULK_DELETE: 50034,
  INVALID_FORM_BODY: 50035,
  OWNER_CANNOT_LEAVE: 50055,
  REQUIRED_COMMUNITY_CHANNEL: 50074,
  THREAD_LOCKED: 50083,
  THREAD_ALREADY_CREATED: 160004,
  AUTOMOD_BLOCKED: 200000,
  BULK_BAN_FAILED: 500000,
  POLL_EXPIRED: 520001,
  NOT_A_POLL: 520006,
} as const;

// ---------------------------------------------------------------------------
// Pessoas, servidores, cargos, expressões
// ---------------------------------------------------------------------------

export type APIUser = {
  id: Snowflake;
  username: string;
  avatar: string | null;
  discriminator: string;
  public_flags: number;
  flags: number;
  bot?: true;
  system?: true;
  banner?: string | null;
  accent_color?: number | null;
  global_name: string | null;
};

export type APIRole = {
  id: Snowflake;
  name: string;
  description: string | null;
  color: number;
  hoist: boolean;
  icon: string | null;
  unicode_emoji: string | null;
  position: number;
  /** Bitfield (`contracts/chat-permissions.ts`) como texto: passa de 2^53. */
  permissions: string;
  managed: boolean;
  mentionable: boolean;
  flags: number;
  tags?: Record<string, unknown>;
};

/** Emoji de reação, enquete ou etiqueta: personalizado (`id`) ou Unicode (`name`). */
export type APIPartialEmoji = {
  id: Snowflake | null;
  name: string | null;
  animated?: boolean;
};

export type APIEmoji = {
  id: Snowflake;
  name: string;
  /** Vazio = todos usam. */
  roles: Snowflake[];
  /** Quem enviou: só para quem cria ou gerencia expressões. */
  user?: APIUser;
  require_colons: boolean;
  managed: boolean;
  animated: boolean;
  available: boolean;
};

export type APISticker = {
  id: Snowflake;
  name: string;
  tags: string;
  /** 2 = do servidor. */
  type: number;
  /** 1 PNG, 2 APNG, 3 Lottie, 4 GIF. */
  format_type: number;
  description: string | null;
  available: boolean;
  guild_id: Snowflake;
  user?: APIUser;
};

/** Figurinha numa mensagem. */
export type APIStickerItem = {
  id: Snowflake;
  name: string;
  format_type: number;
};

export type APIGuild = {
  id: Snowflake;
  name: string;
  icon: string | null;
  description: string | null;
  splash: string | null;
  banner: string | null;
  features: string[];
  owner_id: Snowflake;
  verification_level: number;
  /** 0 todas as mensagens, 1 só menções. */
  default_message_notifications: number;
  explicit_content_filter: number;
  mfa_level: number;
  system_channel_id: Snowflake | null;
  system_channel_flags: number;
  rules_channel_id: Snowflake | null;
  public_updates_channel_id: Snowflake | null;
  roles: APIRole[];
  emojis: APIEmoji[];
  stickers: APISticker[];
};

/** Servidor em `GET /users/@me/guilds`. */
export type APIPartialGuild = {
  id: Snowflake;
  name: string;
  icon: string | null;
  banner: string | null;
  owner: boolean;
  /** Permissões da pessoa pelos cargos (sem overwrites nem castigo). */
  permissions: string;
  features: string[];
};

export type APIGuildMember = {
  user: APIUser;
  nick: string | null;
  avatar: string | null;
  banner: string | null;
  roles: Snowflake[];
  joined_at: string;
  /** Ainda não aceitou as regras: vê e lê, não escreve. */
  pending: boolean;
  flags: number;
  communication_disabled_until: string | null;
};

/** Membro que vai junto de mensagem, reação ou digitação (sem o `user`). */
export type APIPartialGuildMember = Omit<APIGuildMember, 'user'> & {
  user?: APIUser;
};

// ---------------------------------------------------------------------------
// Canais e threads
// ---------------------------------------------------------------------------

export type APIOverwrite = {
  /** Cargo (o @everyone tem o ID do servidor) ou pessoa. */
  id: Snowflake;
  /** 0 cargo, 1 membro. */
  type: number;
  allow: string;
  deny: string;
};

export type APIThreadMetadata = {
  archived: boolean;
  archive_timestamp: string | null;
  auto_archive_duration: number | null;
  locked: boolean;
  invitable?: boolean;
  create_timestamp?: string;
};

export type APIThreadMember = {
  /** ID da thread. */
  id: Snowflake;
  user_id: Snowflake;
  join_timestamp: string;
  flags: number;
  member?: APIGuildMember | null;
};

export type APIForumTag = {
  id: Snowflake;
  name: string;
  moderated: boolean;
  emoji_id: Snowflake | null;
  emoji_name: string | null;
};

/**
 * Canal de qualquer tipo, como o Discord: os campos dependem do tipo
 * (`ChannelType`). Servidor: texto, categoria, anúncio, fórum, mídia;
 * threads; DM e grupo (sem `guild_id`, com `recipients`).
 */
export type APIChannel = {
  id: Snowflake;
  type: number;
  flags: number;
  guild_id?: Snowflake | null;
  name?: string | null;
  parent_id?: Snowflake | null;
  last_message_id?: Snowflake | null;
  last_pin_timestamp?: string | null;
  rate_limit_per_user?: number;
  // Canais do servidor (não thread)
  position?: number;
  permission_overwrites?: APIOverwrite[];
  nsfw?: boolean;
  topic?: string | null;
  default_thread_rate_limit_per_user?: number;
  default_auto_archive_duration?: number;
  // Fórum e mídia
  available_tags?: APIForumTag[];
  default_reaction_emoji?: {
    emoji_id: Snowflake | null;
    emoji_name: string | null;
  } | null;
  default_sort_order?: number | null;
  default_forum_layout?: number;
  // Threads
  owner_id?: Snowflake | null;
  message_count?: number;
  member_count?: number;
  total_message_sent?: number;
  thread_metadata?: APIThreadMetadata;
  applied_tags?: Snowflake[];
  /** `THREAD_CREATE` de quem criou. */
  newly_created?: boolean;
  member?: APIThreadMember;
  // DM e grupo
  recipients?: APIUser[];
  icon?: string | null;
  /** Nosso: canal de voz do Discord que ficou só com o histórico, como texto. */
  legacy_voice?: true;
};

// ---------------------------------------------------------------------------
// Mensagens
// ---------------------------------------------------------------------------

export type APIAttachment = {
  id: Snowflake;
  filename: string;
  size: number;
  url: string;
  proxy_url: string;
  content_type?: string;
  width?: number;
  height?: number;
  title?: string;
  description?: string;
  duration_secs?: number;
  waveform?: string;
  flags?: number;
  placeholder?: string;
};

export type APIEmbedMedia = {
  url: string;
  proxy_url?: string;
  width?: number;
  height?: number;
  content_type?: string;
};

/** Embed do Discord (o histórico tem de link, imagem, vídeo, rich…). */
export type APIEmbed = {
  type?: string;
  title?: string;
  description?: string;
  url?: string;
  timestamp?: string;
  color?: number;
  footer?: { text: string; icon_url?: string; proxy_icon_url?: string };
  image?: APIEmbedMedia;
  thumbnail?: APIEmbedMedia;
  video?: APIEmbedMedia;
  provider?: { name?: string; url?: string };
  author?: {
    name: string;
    url?: string;
    icon_url?: string;
    proxy_icon_url?: string;
  };
  fields?: Array<{ name: string; value: string; inline?: boolean }>;
};

export type APIReaction = {
  emoji: APIPartialEmoji;
  count: number;
  count_details: { burst: number; normal: number };
  burst_colors: string[];
  me_burst: boolean;
  burst_me: boolean;
  me: boolean;
  burst_count: number;
};

export type APIPollMedia = { text?: string; emoji?: APIPartialEmoji };

export type APIPoll = {
  question: APIPollMedia;
  answers: Array<{ answer_id: number; poll_media: APIPollMedia }>;
  expiry: string | null;
  allow_multiselect: boolean;
  layout_type: number;
  results?: {
    is_finalized: boolean;
    answer_counts: Array<{ id: number; count: number; me_voted: boolean }>;
  };
};

/** Resposta (`type` 0) ou encaminhamento (`type` 1). */
export type APIMessageReference = {
  type?: number;
  message_id?: Snowflake;
  channel_id?: Snowflake;
  guild_id?: Snowflake;
  fail_if_not_exists?: boolean;
};

export type APIMessage = {
  id: Snowflake;
  channel_id: Snowflake;
  author: APIUser;
  content: string;
  timestamp: string;
  edited_timestamp: string | null;
  tts: boolean;
  mention_everyone: boolean;
  mentions: APIUser[];
  mention_roles: Snowflake[];
  attachments: APIAttachment[];
  embeds: APIEmbed[];
  reactions?: APIReaction[];
  pinned: boolean;
  type: number;
  flags: number;
  components: unknown[];
  webhook_id?: Snowflake;
  application_id?: Snowflake;
  message_reference?: APIMessageReference;
  /** Resposta e início de thread: a citada (nula se foi apagada). */
  referenced_message?: APIMessage | null;
  /** Encaminhada: o conteúdo da original. */
  message_snapshots?: Array<{ message: Partial<APIMessage> }>;
  sticker_items?: APIStickerItem[];
  poll?: APIPoll;
  interaction_metadata?: Record<string, unknown>;
  call?: Record<string, unknown>;
  /** A thread aberta a partir desta mensagem. */
  thread?: APIChannel;
  /** Posição na thread (o Discord manda nas mensagens de thread). */
  position?: number;
  nonce?: string | number;
  /** Eventos e busca do servidor. */
  guild_id?: Snowflake;
  member?: APIPartialGuildMember | null;
  /** Busca: a mensagem encontrada. */
  hit?: true;
};

/** Fixada: `GET /channels/:id/messages/pins` devolve `{items, has_more}`. */
export type APIPinnedMessage = { pinned_at: string; message: APIMessage };

// ---------------------------------------------------------------------------
// Administração
// ---------------------------------------------------------------------------

export type APIInvite = {
  type: number;
  code: string;
  inviter?: APIUser;
  expires_at: string | null;
  guild: {
    id: Snowflake;
    name: string;
    splash: string | null;
    banner: string | null;
    description: string | null;
    icon: string | null;
    features: string[];
    verification_level: number;
    vanity_url_code: string | null;
    nsfw_level: number;
    premium_subscription_count: number;
  };
  guild_id: Snowflake;
  channel: { id: Snowflake; type: number; name: string | null } | null;
  approximate_member_count?: number;
  approximate_presence_count?: number;
  // Com metadados (criar e listar)
  uses?: number;
  max_uses?: number;
  max_age?: number;
  temporary?: boolean;
  created_at?: string;
  /** Cargos que o convite dá ao entrar. */
  role_ids?: Snowflake[];
  // Ao entrar
  new_member?: boolean;
  show_verification_form?: boolean;
};

export type APIBan = { reason: string | null; user: APIUser };

export type APIAuditLogChange = {
  key: string;
  old_value?: unknown;
  new_value?: unknown;
};

export type APIAuditLogEntry = {
  id: Snowflake;
  guild_id?: Snowflake;
  user_id: Snowflake | null;
  target_id: string | null;
  action_type: number;
  changes?: APIAuditLogChange[];
  options?: Record<string, unknown>;
  reason?: string;
};

export type APIAutoModerationRule = {
  id: Snowflake;
  guild_id: Snowflake;
  name: string;
  creator_id: Snowflake | null;
  event_type: number;
  /** 1 palavras e padrões, 5 excesso de menções. */
  trigger_type: number;
  trigger_metadata: {
    keyword_filter?: string[];
    regex_patterns?: string[];
    allow_list?: string[];
    mention_total_limit?: number;
    mention_raid_protection_enabled?: boolean;
  };
  /** 1 bloquear, 2 alertar num canal, 3 castigar. */
  actions: Array<{ type: number; metadata?: Record<string, unknown> }>;
  enabled: boolean;
  exempt_roles: Snowflake[];
  exempt_channels: Snowflake[];
};

export type APIAuditLog = {
  audit_log_entries: APIAuditLogEntry[];
  users: APIUser[];
  integrations: unknown[];
  webhooks: unknown[];
  guild_scheduled_events: Array<{ id: Snowflake; name: string }>;
  threads: APIChannel[];
  application_commands: unknown[];
  auto_moderation_rules: APIAutoModerationRule[];
};

export type APIGuildScheduledEvent = {
  id: Snowflake;
  guild_id: Snowflake;
  channel_id: Snowflake | null;
  creator_id: Snowflake | null;
  name: string;
  description: string | null;
  scheduled_start_time: string;
  scheduled_end_time: string | null;
  privacy_level: number;
  /** 1 agendado, 2 acontecendo, 3 terminou, 4 cancelado. */
  status: number;
  /** 3 = externo (o único sem voz). */
  entity_type: number;
  entity_id: Snowflake | null;
  entity_metadata: { location?: string } | null;
  creator?: APIUser;
  user_count?: number;
  image: string | null;
  recurrence_rule: Record<string, unknown> | null;
};

/** `GET /users/:id/profile` (como o Discord): o perfil completo de alguém. */
export type APIUserProfile = {
  user: APIUser & { bio: string | null };
  user_profile: {
    bio: string | null;
    accent_color: number | null;
    banner: string | null;
  };
  guild_member?: APIGuildMember;
  mutual_guilds: Array<{ id: Snowflake; nick: string | null }>;
};

export type APIWelcomeScreen = {
  description: string | null;
  welcome_channels: Array<{
    channel_id: Snowflake;
    description: string;
    emoji_id: Snowflake | null;
    emoji_name: string | null;
  }>;
};

export type APIMemberVerification = {
  version: string | null;
  form_fields: Array<{
    field_type: 'TERMS';
    label: string;
    values: string[];
    required: boolean;
  }>;
  description: string | null;
};

/** Estatísticas (nossas): `GET /guilds/:id/statistics`. */
export type APIGuildStatistics = {
  guild_id: Snowflake;
  days: number;
  since: string;
  time_zone: string;
  members: { total: number; joined: number; active: number };
  messages: { total: number };
  per_day: Array<{
    date: string;
    joined: number;
    messages: number;
    active_members: number;
  }>;
  top_channels: Array<{
    channel_id: Snowflake;
    name: string | null;
    messages: number;
    active_members: number;
  }>;
};

/** `GET /users/@me/notes/:id`. */
export type APIUserNote = {
  user_id: Snowflake;
  note_user_id: Snowflake;
  note: string;
};

export type APISearchResults = {
  total_results: number;
  /** Cada item é uma lista com a mensagem encontrada (`hit: true`). */
  messages: APIMessage[][];
  threads: APIChannel[];
  members: APIGuildMember[];
  doing_deep_historical_index: boolean;
  analytics_id: string;
};

// ---------------------------------------------------------------------------
// Preferências e leitura
// ---------------------------------------------------------------------------

export type APIQuietHours = {
  enabled: boolean;
  start: string;
  end: string;
  days: number[];
  time_zone: string;
};

/** `GET /users/@me/settings` (o `user_settings` do READY). */
export type APIUserSettings = {
  status: 'online' | 'idle' | 'dnd' | 'invisible';
  custom_status: {
    text?: string | null;
    emoji_id?: Snowflake | null;
    emoji_name?: string | null;
    expires_at?: string | null;
  } | null;
  theme: 'dark' | 'light' | 'system';
  message_display_compact: boolean;
  locale: string;
  inline_attachment_media: boolean;
  inline_embed_media: boolean;
  gif_auto_play: boolean;
  render_embeds: boolean;
  render_reactions: boolean;
  animate_emoji: boolean;
  animate_stickers: number;
  enable_tts_command: boolean;
  convert_emoticons: boolean;
  developer_mode: boolean;
  default_guilds_restricted: boolean;
  restricted_guilds: Snowflake[];
  guild_positions: Snowflake[];
  guild_folders: Array<{
    id?: number | null;
    guild_ids: Snowflake[];
    name?: string | null;
    color?: number | null;
  }>;
  timezone_offset: number;
  /** Nosso: nada notifica nesse intervalo. */
  quiet_hours: APIQuietHours;
};

export type APIMuteConfig = {
  end_time: string | null;
  selected_time_window: number | null;
};

/** `user_guild_settings` do Discord; DMs com `guild_id` nulo. */
export type APIUserGuildSettings = {
  guild_id: Snowflake | null;
  muted: boolean;
  mute_config: APIMuteConfig | null;
  mute_scheduled_events: boolean;
  /** 0 todas, 1 só menções, 2 nada, 3 o padrão do servidor. */
  message_notifications: 0 | 1 | 2 | 3;
  notify_highlights: number;
  flags: number;
  suppress_everyone: boolean;
  suppress_roles: boolean;
  mobile_push: boolean;
  hide_muted_channels: boolean;
  channel_overrides: Array<{
    channel_id: Snowflake;
    muted: boolean;
    mute_config: APIMuteConfig | null;
    /** 3 = herda do servidor. */
    message_notifications: 0 | 1 | 2 | 3;
    collapsed: boolean;
  }>;
  version: number;
};

/** Até onde a pessoa leu um canal (`read_state` do READY). */
export type APIReadState = {
  id: Snowflake;
  last_message_id: Snowflake | null;
  mention_count: number;
  last_pin_timestamp: string | null;
};

// ---------------------------------------------------------------------------
// Gateway
// ---------------------------------------------------------------------------

export type APIPresence = {
  user?: { id: Snowflake };
  user_id?: Snowflake;
  guild_id?: Snowflake;
  status: 'online' | 'idle' | 'dnd' | 'offline';
  client_status: Partial<Record<'web' | 'desktop' | 'mobile', string>>;
  activities: unknown[];
};

/** Servidor no READY e no `GUILD_CREATE` (com canais, threads e membros). */
export type APIGatewayGuild = APIGuild & {
  joined_at: string | null;
  member_count: number;
  large: boolean;
  lazy: boolean;
  unavailable: boolean;
  channels: APIChannel[];
  threads: APIChannel[];
  members: APIGuildMember[];
};

export type APIGatewayReady = {
  v: number;
  user: APIUser;
  session_id: string;
  resume_gateway_url: string;
  guilds: APIGatewayGuild[];
  private_channels: APIChannel[];
  read_state: { entries: APIReadState[]; partial: boolean; version: number };
  user_guild_settings: {
    entries: APIUserGuildSettings[];
    partial: boolean;
    version: number;
  };
  user_settings: APIUserSettings;
  relationships: unknown[];
  guild_join_requests: unknown[];
};

export type APIGatewayReadySupplemental = {
  guilds: Array<{ id: Snowflake }>;
  merged_presences: { guilds: APIPresence[][]; friends: unknown[] };
};

type WithGuild = { guild_id?: Snowflake };

/** O `d` de cada evento do gateway. */
export type GatewayDispatchPayloads = {
  READY: APIGatewayReady;
  READY_SUPPLEMENTAL: APIGatewayReadySupplemental;
  RESUMED: Record<string, never>;
  GUILD_CREATE: APIGatewayGuild;
  GUILD_UPDATE: APIGuild;
  GUILD_DELETE: { id: Snowflake; unavailable?: boolean };
  GUILD_ROLE_CREATE: { guild_id: Snowflake; role: APIRole };
  GUILD_ROLE_UPDATE: { guild_id: Snowflake; role: APIRole };
  GUILD_ROLE_DELETE: { guild_id: Snowflake; role_id: Snowflake };
  GUILD_MEMBER_ADD: APIGuildMember & { guild_id: Snowflake };
  GUILD_MEMBER_UPDATE: APIGuildMember & { guild_id: Snowflake };
  GUILD_MEMBER_REMOVE: { guild_id: Snowflake; user: APIUser };
  GUILD_BAN_ADD: { guild_id: Snowflake; user: APIUser };
  GUILD_BAN_REMOVE: { guild_id: Snowflake; user: APIUser };
  GUILD_EMOJIS_UPDATE: { guild_id: Snowflake; emojis: APIEmoji[] };
  GUILD_STICKERS_UPDATE: { guild_id: Snowflake; stickers: APISticker[] };
  GUILD_AUDIT_LOG_ENTRY_CREATE: APIAuditLogEntry;
  GUILD_SCHEDULED_EVENT_CREATE: APIGuildScheduledEvent;
  GUILD_SCHEDULED_EVENT_UPDATE: APIGuildScheduledEvent;
  GUILD_SCHEDULED_EVENT_DELETE: APIGuildScheduledEvent;
  GUILD_SCHEDULED_EVENT_USER_ADD: {
    guild_scheduled_event_id: Snowflake;
    user_id: Snowflake;
    guild_id: Snowflake;
  };
  GUILD_SCHEDULED_EVENT_USER_REMOVE: {
    guild_scheduled_event_id: Snowflake;
    user_id: Snowflake;
    guild_id: Snowflake;
  };
  AUTO_MODERATION_RULE_CREATE: APIAutoModerationRule;
  AUTO_MODERATION_RULE_UPDATE: APIAutoModerationRule;
  AUTO_MODERATION_RULE_DELETE: APIAutoModerationRule;
  AUTO_MODERATION_ACTION_EXECUTION: {
    guild_id: Snowflake;
    action: { type: number; metadata: Record<string, unknown> };
    rule_id: Snowflake;
    rule_trigger_type: number;
    user_id: Snowflake;
    channel_id: Snowflake;
    message_id?: Snowflake;
    alert_system_message_id?: Snowflake;
    content: string;
    matched_keyword: string | null;
    matched_content: string | null;
  };
  INVITE_CREATE: {
    channel_id: Snowflake;
    code: string;
    created_at: string;
    guild_id: Snowflake;
    inviter?: APIUser;
    max_age: number;
    max_uses: number;
    temporary: boolean;
    uses: number;
    expires_at: string | null;
    type: number;
  };
  INVITE_DELETE: { channel_id: Snowflake; guild_id: Snowflake; code: string };
  CHANNEL_CREATE: APIChannel;
  CHANNEL_UPDATE: APIChannel;
  CHANNEL_DELETE: APIChannel;
  CHANNEL_PINS_UPDATE: WithGuild & {
    channel_id: Snowflake;
    last_pin_timestamp: string | null;
  };
  CHANNEL_RECIPIENT_ADD: { channel_id: Snowflake; user: APIUser };
  CHANNEL_RECIPIENT_REMOVE: { channel_id: Snowflake; user: APIUser };
  THREAD_CREATE: APIChannel;
  THREAD_UPDATE: APIChannel;
  THREAD_DELETE: {
    id: Snowflake;
    guild_id: Snowflake;
    parent_id: Snowflake | null;
    type: number;
  };
  THREAD_MEMBER_UPDATE: APIThreadMember & { guild_id: Snowflake };
  THREAD_MEMBERS_UPDATE: {
    id: Snowflake;
    guild_id: Snowflake;
    member_count: number;
    added_members?: APIThreadMember[];
    removed_member_ids?: Snowflake[];
  };
  MESSAGE_CREATE: APIMessage;
  MESSAGE_UPDATE: APIMessage;
  MESSAGE_DELETE: WithGuild & { id: Snowflake; channel_id: Snowflake };
  MESSAGE_DELETE_BULK: WithGuild & { ids: Snowflake[]; channel_id: Snowflake };
  MESSAGE_ACK: {
    channel_id: Snowflake;
    message_id: Snowflake;
    mention_count: number;
    manual: boolean;
    version: number;
  };
  MESSAGE_REACTION_ADD: WithGuild & {
    user_id: Snowflake;
    channel_id: Snowflake;
    message_id: Snowflake;
    message_author_id?: Snowflake;
    emoji: APIPartialEmoji;
    burst: boolean;
    burst_colors?: string[];
    type: number;
    member?: APIPartialGuildMember | null;
  };
  MESSAGE_REACTION_REMOVE: WithGuild & {
    user_id: Snowflake;
    channel_id: Snowflake;
    message_id: Snowflake;
    emoji: APIPartialEmoji;
    burst: boolean;
    type: number;
  };
  MESSAGE_REACTION_REMOVE_ALL: WithGuild & {
    channel_id: Snowflake;
    message_id: Snowflake;
  };
  MESSAGE_REACTION_REMOVE_EMOJI: WithGuild & {
    channel_id: Snowflake;
    message_id: Snowflake;
    emoji: APIPartialEmoji;
  };
  MESSAGE_POLL_VOTE_ADD: WithGuild & {
    user_id: Snowflake;
    channel_id: Snowflake;
    message_id: Snowflake;
    answer_id: number;
  };
  MESSAGE_POLL_VOTE_REMOVE: WithGuild & {
    user_id: Snowflake;
    channel_id: Snowflake;
    message_id: Snowflake;
    answer_id: number;
  };
  TYPING_START: WithGuild & {
    channel_id: Snowflake;
    user_id: Snowflake;
    timestamp: number;
    member?: APIGuildMember | null;
  };
  PRESENCE_UPDATE: APIPresence & { user: { id: Snowflake } };
  USER_SETTINGS_UPDATE: Partial<APIUserSettings>;
  USER_GUILD_SETTINGS_UPDATE: APIUserGuildSettings;
  /** Nota no perfil de alguém mudou (só para a própria pessoa); vazia = apagada. */
  USER_NOTE_UPDATE: { id: Snowflake; note: string };
  /** Só para a própria pessoa (perfil mudou); os outros recebem `GUILD_MEMBER_UPDATE`. */
  USER_UPDATE: APIUser & { bio?: string | null };
};
